import { useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Seccion } from '@/components/Card';
import { buscarActualizacion, forzarActualizacion, hayConexion } from '@/lib/recovery';

type Estado =
  | { fase: 'inactivo' }
  | { fase: 'buscando' }
  | { fase: 'hayNueva'; mensaje: string }
  | { fase: 'alDia'; mensaje: string }
  | { fase: 'aplicando' };

/**
 * Búsqueda manual de versión nueva (Req. 2.5, 2.9).
 *
 * MOTIVO DE EXISTIR: en iOS la app instalada queda suspendida en memoria y al
 * reabrirla no hace una carga real de la página, así que el aviso automático
 * puede no aparecer aunque exista una versión nueva.
 *
 * Origen: Control de Negocio de Comida, con los textos adaptados.
 */
export function BuscarActualizacion() {
  const [estado, setEstado] = useState<Estado>({ fase: 'inactivo' });

  async function buscar() {
    setEstado({ fase: 'buscando' });
    const resultado = await buscarActualizacion();
    setEstado(
      resultado.hayNueva
        ? { fase: 'hayNueva', mensaje: resultado.mensaje }
        : { fase: 'alDia', mensaje: resultado.mensaje },
    );
  }

  async function aplicar() {
    setEstado({ fase: 'aplicando' });
    const resultado = await forzarActualizacion();
    if (!resultado.exito) {
      setEstado({ fase: 'alDia', mensaje: resultado.mensaje });
    }
  }

  const ocupado = estado.fase === 'buscando' || estado.fase === 'aplicando';

  return (
    <Seccion
      titulo="Versión de la app"
      descripcion="En iPhone la app instalada no siempre revisa sola si hay algo nuevo. Buscar necesita internet."
    >
      <Button variante="secundario" disabled={ocupado} onClick={() => void buscar()} icono={Search}>
        {estado.fase === 'buscando' ? 'Buscando…' : 'Buscar actualización'}
      </Button>

      {estado.fase === 'alDia' && (
        <Banner tono={hayConexion() ? 'exito' : 'aviso'} titulo={estado.mensaje} />
      )}

      {estado.fase === 'hayNueva' && (
        <div className="space-y-2">
          <Banner tono="info" titulo={estado.mensaje}>
            Al actualizar se recarga la app. Tus datos no se tocan.
          </Banner>
          <Button disabled={ocupado} onClick={() => void aplicar()} icono={RefreshCw}>
            Actualizar ahora
          </Button>
        </div>
      )}

      {estado.fase === 'aplicando' && (
        <Banner tono="info" titulo="Actualizando…">
          No cierres la app.
        </Banner>
      )}
    </Seccion>
  );
}
